import IterableDataProcessor from "../entities/IterableDataProcessor.js";
import RankingStat from "../entities/RankingStat.js";
import BattingRankingStats from "../enums/BattingRankingStats.js";
import GeneralRankingStats from "../enums/GeneralRankingStats.js";
import PitchingRankingStats from "../enums/PitchingRankingStats.js";
import RankingScopeContext from "../enums/RankingScopeContext.js";
import RunningRankingStats from "../enums/RunningRankingStats.js";
import DerivativeRankingStatCalculatorSelector from "../selectors/DerivativeRankingStatCalculatorSelector.js";
import EnumValueProvider from "./EnumValueProvider.js";

class DerivativeRankingStatsProvider {
    constructor(strategySelector, customResultsProcessor, throwOnErrors) {
        if (!(strategySelector instanceof DerivativeRankingStatCalculatorSelector)) {
            throw new TypeError('strategySelector must be an instance inheriting from DerivativeRankingStatCalculatorSelector class');
        }
        this.strategySelector = strategySelector;
        this.throwOnErrors = !!throwOnErrors;

        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
    }

    /**
     * 
     * @param {RankingStat[]} baseRankingStats 
     * @param {string[]} expectedParameters 
     * @param {string} context 
     * @param {boolean} [includeDefault] 
     * @param {boolean} [returnOnlyExpected] 
     * @returns 
     */
    calculate(baseRankingStats, expectedParameters, context, includeDefault, returnOnlyExpected) {
        if (!(baseRankingStats instanceof Array)) {
            throw new TypeError('baseRankingStats must be an array containing ranking stats parameters');
        }

        if (!(expectedParameters instanceof Array)) {
            throw new TypeError('expectedParameters must be an array containing objects from BattingRankingStats or PitchingRankingStats enums');
        }

        if (typeof(context) === undefined) {
            throw new TypeError('context must be of string type, value should be from RankingScopeContext Enum');
        }

        if (typeof(includeDefault) === 'undefined') {
            includeDefault = true;
        }
        else if (typeof(includeDefault) !== 'boolean') {
            throw new TypeError('includeDefault must be either boolean or undefined to use default - true');
        }

        if (typeof(returnOnlyExpected) === 'undefined') {
            returnOnlyExpected = true;
        }
        else if (typeof(returnOnlyExpected) !== 'boolean') {
            throw new TypeError('returnOnlyExpected must be either boolean or undefined to use default - true');
        }

        // shortcut for enum
        const rankingStatEnum = this.getRankingStatsEnum(context);
        if (!rankingStatEnum) {
            throw new Error(`Unable to map context ${context} to any known ranking stats enum`);
        }

        // unify params
        const outputParams = expectedParameters.map(
            p => typeof(p) === 'object' ? p.key : p
        );

        // copy of provided ranking stats
        const allRankingStats = [].concat(baseRankingStats);

        // so now we need to build a tree of dependencies
        for (let i = 0; i < outputParams.length; i++) {
            const parameterKey = outputParams[i];
            // TODO - this will throw, but IMO it should stay like that, it could mean that somebody is mixing enums here
            const parameter = EnumValueProvider.getValue(parameterKey, rankingStatEnum);

            if (allRankingStats.some(rs => rs.parameter === parameter.key)) {
                // skip calculation if we already have it
                continue;
            }

            // let's build an recursive tree of dependencies if calculation requires nested processing
            const dependencyTree = this.buildDependencyTree(parameter, allRankingStats);

            if (dependencyTree === null || dependencyTree.strategy === null) {
                if (includeDefault) {
                    allRankingStats.push(RankingStat.getDefault(parameter, 0));
                }

                continue;
            }

            this.processDependencies(dependencyTree, allRankingStats, includeDefault);
        }

        if (returnOnlyExpected) {
            return allRankingStats.filter(r => outputParams.includes(r.parameter));
        }

        return allRankingStats;
    }

    listDependencies(expectedParameters, context) {
        if (!(expectedParameters instanceof Array)) {
            throw new TypeError('expectedParameters must be an array containing objects from BattingRankingStats or PitchingRankingStats enums');
        }

        if (typeof(context) === undefined) {
            throw new TypeError('context must be of string type, value should be from RankingScopeContext Enum');
        }

        // shortcut for enum
        const rankingStatEnum = this.getRankingStatsEnum(context);
        if (!rankingStatEnum) {
            throw new Error(`Unable to map context ${context} to any known ranking stats enum`);
        }

        // unify params
        const outputParams = expectedParameters.map(
            p => typeof(p) === 'object' ? p.key : p
        );

        const dependencies = [];

         // so now we need to build a tree of dependencies
         for (let i = 0; i < outputParams.length; i++) {
            const parameterKey = outputParams[i];
            // TODO - this will throw, but IMO it should stay like that, it could mean that somebody is mixing enums here
            const parameter = EnumValueProvider.getValue(parameterKey, rankingStatEnum);

            // let's build an recursive tree of dependencies if calculation requires nested processing
            const dependencyTree = this.buildDependencyTree(parameter, []);
            if (dependencyTree === null) {
                continue;
            }

            this.analyzeDependencies(dependencyTree, dependencies);
        }

        // return only unique values
        return dependencies.filter((v, i, a) => a.indexOf(v) === i);
    }

    analyzeDependencies(dependencyBranch, dependencies) {
        for (let i = 0; i < dependencyBranch.dependencies.length; i++) {
            const dependency = dependencyBranch.dependencies[i];
            this.analyzeDependencies(dependency, dependencies);
        }

        if (dependencyBranch.dependencies.length === 0 || !dependencyBranch.strategy) {
            dependencies.push(dependencyBranch.parameter);
        }
    }

    processDependencies(dependencyBranch, allRankingStats, includeDefault) {
        if (!dependencyBranch.strategy) {
            // if there is not strategy we should have this value in allRankingStats
            return;
        }
        if (allRankingStats.some(rs => rs.parameter === dependencyBranch.parameter.key)) {
            // skip calculation if we already have it
            return;
        }

        for (let i = 0; i < dependencyBranch.dependencies.length; i++) {
            const dependency = dependencyBranch.dependencies[i];
            this.processDependencies(dependency, allRankingStats, includeDefault);
        }

        try {
            const calculatedParam = dependencyBranch.strategy.calculate(
                dependencyBranch.parameter, allRankingStats, includeDefault
            );

            if (calculatedParam) {
                allRankingStats.push(calculatedParam);
            }
            else if (includeDefault) {
                allRankingStats.push(RankingStat.getDefault(dependencyBranch.parameter, 0));
            }
        } catch (error) {
            if (this.throwOnErrors) {
                throw error;
            }

            if (includeDefault) {
                allRankingStats.push(RankingStat.getDefault(dependencyBranch.parameter, 0));
            }
        }
    }

    buildDependencyTree(parameter, allRankingStats) {
        // TODO - protection against infinite loop here
        const strategies = this.strategySelector.selectAll(parameter);
        if (!strategies || strategies.length === 0) {
            return {
                strategy: null, parameter, dependencies: []
            };
        }

        // if we have possible strategy, we now need to look what parameters will be needed
        for (let s = 0; s < strategies.length; s++) {
            const strategy = strategies[s];
            const dependencyTreeForStrategy = {
                strategy, parameter, dependencies: []
            };

            const dependencies = strategy.getDependencies(parameter);
            for (let d = 0; d < dependencies.length; d++) {
                const dependency = dependencies[d];
                let dependencyBranch = this.buildDependencyTree(dependency, allRankingStats);

                dependencyTreeForStrategy.dependencies.push(dependencyBranch);
            }

            return dependencyTreeForStrategy;
        }

        return null;
    }

    getRankingStatsEnum(context) {
        let rankingStatsEnum = null;
        switch (context) {
            case RankingScopeContext.Pitching.key:
                rankingStatsEnum = PitchingRankingStats;
                break;

            case RankingScopeContext.Batting.key:
                rankingStatsEnum = BattingRankingStats;
                break;

            case RankingScopeContext.Running.key:
                rankingStatsEnum = RunningRankingStats;
                break;

            case RankingScopeContext.General.key:
                rankingStatsEnum = GeneralRankingStats;
                break;
        }

        return rankingStatsEnum;
    }
}

export default DerivativeRankingStatsProvider;